import type { FloorOutlineMode, GeoJSONPolygon } from "@sitescene/types";

import {
	coordinatesToConvexHullPolygon,
	normalizeGeoJsonPolygon,
	normalizeZoneBoundary,
} from "@sitescene/shared";

import type { ShadowFloor, ShadowZone } from "../../types/location-map";

import { getRefId } from "../../utils/location/installation-ids";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";

/** Gray outline so a floor stays distinct from zone fills until a color is chosen. */
export const DEFAULT_FLOOR_OUTLINE_COLOR = "#667085";
export const DEFAULT_FLOOR_BORDER_WIDTH = 2;
export const MIN_FLOOR_BORDER_WIDTH = 0;
export const MAX_FLOOR_BORDER_WIDTH = 12;
/** Translucent fill used while the outline is being edited. */
export const FLOOR_EDIT_FILL_OPACITY = 0.32;

export type FloorOutlineAppearance = {
	borderWidth: number;
	color: string;
	fill: boolean;
};

export const normalizeFloorBorderWidth = (value?: number): number => {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return DEFAULT_FLOOR_BORDER_WIDTH;
	}
	return Math.min(
		MAX_FLOOR_BORDER_WIDTH,
		Math.max(MIN_FLOOR_BORDER_WIDTH, Math.round(value)),
	);
};

export const resolveFloorOutlineAppearance = (
	floor?: null | {
		borderWidth?: number;
		color?: string;
		fill?: boolean;
	},
): FloorOutlineAppearance => {
	const color = floor?.color?.trim().toUpperCase() ?? "";
	return {
		borderWidth: normalizeFloorBorderWidth(floor?.borderWidth),
		color: /^#[0-9A-F]{6}$/.test(color) ? color : DEFAULT_FLOOR_OUTLINE_COLOR,
		fill: floor?.fill !== false,
	};
};

export const floorBoundaryFeatureId = (floorId: string): string =>
	`floor-boundary:${floorId}`;

export const floorIdFromBoundaryFeature = (
	featureId: string | undefined,
): null | string => {
	if (!featureId?.startsWith("floor-boundary:")) return null;
	const floorId = featureId.slice("floor-boundary:".length);
	return floorId.length > 0 ? floorId : null;
};

/**
 * Saved mode, or a fallback for floors created before outline mode existed.
 * An image means image mode. A stored polygon means custom. Otherwise auto.
 */
export const resolveFloorOutlineMode = (
	floor: null | ShadowFloor | undefined,
): FloorOutlineMode => {
	if (
		floor?.outlineMode === "auto" ||
		floor?.outlineMode === "custom" ||
		floor?.outlineMode === "image"
	) {
		return floor.outlineMode;
	}
	if (floor?.image) return "image";
	if (floor?.boundary?.type === "Polygon") return "custom";
	return "auto";
};

const pushPositions = (points: [number, number][], positions: unknown) => {
	if (!Array.isArray(positions)) return;

	for (const position of positions) {
		if (!isValidCoordinatePair(position)) continue;
		points.push([position[0], position[1]]);
	}
};

/** Points that Auto should wrap: each child zone's outline, or its sensors. */
const pointsForFloorZones = (
	floorId: string,
	zones: readonly ShadowZone[],
): [number, number][] => {
	const points: [number, number][] = [];

	for (const zone of zones) {
		if (getRefId(zone.floor) !== floorId) continue;

		const boundary = normalizeZoneBoundary(zone.boundary);
		if (boundary?.type === "Polygon") {
			pushPositions(points, boundary.coordinates[0]);
			continue;
		}
		if (boundary?.type === "LineString") {
			pushPositions(points, boundary.coordinates);
			continue;
		}

		for (const sensor of zone.sensors ?? []) {
			pushPositions(points, [sensor.geolocation?.coordinates ?? []]);
		}
	}

	return points;
};

/** Closed shape around the zones on a floor. Null when the floor has no geometry. */
export const buildAutoFloorOutline = (
	floorId: string,
	zones: readonly ShadowZone[],
): GeoJSONPolygon | null => {
	const feature = coordinatesToConvexHullPolygon(
		pointsForFloorZones(floorId, zones),
	);
	if (!feature || feature.geometry.type !== "Polygon") return null;
	return normalizeGeoJsonPolygon(feature.geometry);
};

const customFloorOutline = (floor: ShadowFloor): GeoJSONPolygon | null => {
	if (floor.boundary?.type !== "Polygon") return null;
	return normalizeGeoJsonPolygon(floor.boundary);
};

const outlineFeature = (
	floor: ShadowFloor,
	polygon: GeoJSONPolygon,
): GeoJSON.Feature<GeoJSON.Polygon> => {
	const appearance = resolveFloorOutlineAppearance(floor);
	return {
		geometry: polygon,
		id: floorBoundaryFeatureId(floor.id),
		properties: {
			boundsSource: "manual",
			floorId: floor.id,
			...(appearance.fill ? {} : { fillOpacity: 0 }),
			lineWidth: appearance.borderWidth,
			overlayKind: "floor",
			overlayStyle: "fill",
			zoneColor: appearance.color,
		},
		type: "Feature",
	};
};

/**
 * Floor shapes for the map.
 * Auto wraps child zones. Custom uses the saved polygon. Image draws no shape.
 */
export const buildFloorBoundaryOverlays = (
	floors: readonly ShadowFloor[],
	zones: readonly ShadowZone[],
): GeoJSON.Feature<GeoJSON.Polygon>[] =>
	floors.flatMap((floor) => {
		const mode = resolveFloorOutlineMode(floor);
		const polygon =
			mode === "custom"
				? customFloorOutline(floor)
				: mode === "auto"
					? buildAutoFloorOutline(floor.id, zones)
					: null;
		if (!polygon) return [];
		return [outlineFeature(floor, polygon)];
	});
